/* eslint-disable no-restricted-syntax */
import { Button } from '@finn/design-system/atoms/button';
import { Spinner } from '@finn/design-system/atoms/spinner';
import { ArrowForwardIos } from '@finn/design-system/icons/arrow-forward-ios';
import { Features, useGetFeature } from '@finn/ua-featureflags';
import { TrackingEventName } from '@finn/ua-tracking';
import {
  cn,
  getCloudinaryImgUrl,
  getCloudinarySrcSet,
  trackEvent,
} from '@finn/ui-utils';
import { useRouter } from 'next/navigation';
import React, { useRef, useState } from 'react';

import HeroFilters from './HeroFilters';
import { Hero2CosmicMetadata } from './metaData';
import { PlaceholderText } from './PlaceholderText';

type Props = {
  data: Hero2CosmicMetadata;
  title: string;
};

type AiResponse = {
  url: string;
  total_results: number;
  not_found?: boolean;
  wrong_prompt?: boolean;
  help_center?: boolean;
  reason?: string;
};

export const HeroWithFilters: React.FC<Props> = ({ data, title }) => {
  const [prompt, setPrompt] = useState('');
  const [firedSearchStartedEvent, setFiredSearchStartedEvent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isExecutingAi, setIsExecutingAi] = useState(false);
  const [searchFocused, setIsSearchFocused] = useState(false);
  const aiVariant = useGetFeature(Features.ExpAiSearch);
  const isSearchExp = aiVariant === 'b' || aiVariant === 'c';
  const isBVariant = aiVariant === 'b';
  const isCVariant = aiVariant === 'c';
  const { push } = useRouter();

  const handleSearch = async () => {
    inputRef.current?.blur();
    setIsExecutingAi(true);

    if (!prompt) {
      setTimeout(() => {
        push('/subscribe');
      }, 100);

      return;
    }

    trackEvent(TrackingEventName.SEARCH_SUBMITTED, {
      query: prompt,
    });

    setTimeout(async () => {
      try {
        const response = await fetch('/api/ai-search', {
          method: 'POST',
          body: JSON.stringify({ message: prompt }),
        });

        const {
          url,
          total_results,
          not_found,
          wrong_prompt,
          reason,
          help_center,
        } = (await response.json()) as AiResponse;

        trackEvent(TrackingEventName.SEARCH_RESULT, {
          query: prompt,
          total_results,
          not_found,
          help_center,
          wrong_prompt,
          reason,
        });

        const reasonUrl = `${url?.includes('?') ? '&' : '?'}reason=${reason ? encodeURIComponent(reason) : ''}`;

        if (help_center) {
          setTimeout(() => {
            window.location.href = `https://support.finn.com/hc/de-de/search?utf8=%E2%9C%93&query=${encodeURIComponent(prompt)}`;
          }, 100);

          return;
        }

        if (url) {
          push(`${url}${reasonUrl}&reason_type=success`);

          return;
        }

        if (not_found) {
          push(`/subscribe?reason_type=not_found`);

          return;
        }
        if (wrong_prompt) {
          push(`/subscribe${reasonUrl}&reason_type=wrong_prompt`);

          return;
        }
        if (total_results === 0) {
          push(`/subscribe?reason_type=no_results`);
        }
      } catch (error) {
        console.error(error);
        setIsExecutingAi(false);
      }
    });
  };

  if (!data) return null;

  const imgURL =
    'https://imgix.cosmicjs.com/9ee09850-2fe9-11f0-842a-c7ebc7727e7e-Home-Hero-Background-Image.jpg'; // TODO; replace in split hero after campaign ends
  const mobileImageUrl =
    'https://imgix.cosmicjs.com/84d4d9c0-2fea-11f0-842a-c7ebc7727e7e-Home-Hero-Background-Image-Mobile.jpg';

  const mobileSrcSet = getCloudinarySrcSet(mobileImageUrl, [480, 800]);
  const mobileSrc = getCloudinaryImgUrl(mobileImageUrl, { w: 480, dpr: 2 });

  const srcSet = getCloudinarySrcSet(imgURL, [1200, 1600]);
  const src = getCloudinaryImgUrl(imgURL, { w: 1200, dpr: 2 });

  return (
    <div className={'relative h-[622px] md:h-[732px] lg:h-[822px]'}>
      <img
        draggable={false}
        src={mobileSrc}
        srcSet={mobileSrcSet}
        alt={data.hero_image_alt_text}
        className={`-z-overlay absolute inset-0 h-full w-full object-cover sm:hidden`}
      />
      <img
        draggable={false}
        src={src}
        srcSet={srcSet}
        alt={data.hero_image_alt_text}
        className={`-z-overlay absolute inset-0 hidden h-full w-full object-cover sm:block`}
      />
      <div className="container" data-testid="hero2">
        <div
          className={cn('-mb-10 grid gap-4 md:-mb-24', {
            'sm:grid-cols-[400px_1fr] md:grid-cols-[500px_1fr]': !isSearchExp,
            'sm:grid-cols-[600px_1fr] sm:pt-8 md:grid-cols-[750px_1fr]':
              isBVariant,
            'sm:grid-cols-[600px_1fr] sm:pt-8 md:grid-cols-[600px_1fr]':
              isCVariant,
          })}
        >
          <div
            className={cn('w-full', {
              'mt-[124px]': !isSearchExp,
              'mt-12 sm:mt-[260px]': isSearchExp,
            })}
          >
            <h1
              className={cn(
                'mobile-t2-semibold md:web-t2-semibold relative mb-6 text-left text-white',
                {
                  'sm:pr-40': isBVariant,
                }
              )}
              data-cy="hero-title"
            >
              <span
                className={`${searchFocused && !isExecutingAi ? '' : 'opacity-0'} mobile-t4-semibold md:web-t3-semibold absolute -top-2 left-0 transition-opacity duration-300 md:top-2`}
              >
                Beschreibe dein Traumauto - Modell, Kraftstoff, Budget oder
                Ausstattung!
              </span>
              <span
                className={`${isExecutingAi ? '' : 'opacity-0'} mobile-t4-semibold md:web-t3-semibold absolute left-0 top-6 transition-opacity duration-300 md:top-2`}
              >
                Eine Sekunde, ich suche nach Ihrem perfekten Auto!
              </span>
              <span
                className={`${searchFocused || isExecutingAi ? 'opacity-0' : ''} transition-opacity duration-300`}
              >
                {isSearchExp ? 'Welches Auto möchtest du fahren?' : title}
              </span>
            </h1>

            {isSearchExp ? (
              <form
                action={handleSearch}
                className="[&:has(:focus-visible)]:shadow-trustedBlue relative rounded-2xl bg-white transition-shadow duration-300 [&:has(:focus-visible)]:shadow-lg [&:has(:focus-visible)_#placeholder]:opacity-0"
              >
                {isExecutingAi && (
                  <div
                    className={cn(
                      'z-overlay border-trustedBlue shadow-trustedBlue absolute left-0 top-0 h-full w-full animate-pulse rounded-2xl border-2 shadow-lg'
                    )}
                  />
                )}
                <div className="relative h-36">
                  <div className="absolute left-0 right-14 top-0 h-full p-4">
                    <PlaceholderText
                      className={`transition-opacity duration-100 ${
                        prompt.length > 0 ? 'opacity-0' : ''
                      }`}
                    />
                    <textarea
                      onFocus={() => {
                        setIsSearchFocused(true);
                      }}
                      onBlur={() => {
                        setIsSearchFocused(false);
                      }}
                      className={cn(
                        'placeholder-iron z-overlay absolute left-0 top-0 h-full w-full p-4 focus:placeholder-opacity-100',
                        'box-border border-none bg-transparent outline-none file:border-0',
                        `body-16-regular disabled:bg-snow disabled:text-iron focus resize-none`
                      )}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      value={prompt}
                      onChange={(e) => {
                        if (!firedSearchStartedEvent) {
                          setFiredSearchStartedEvent(true);
                          trackEvent(TrackingEventName.SEARCH_STARTED, {});
                        }

                        setPrompt(e.target.value);
                      }}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="absolute bottom-4 right-4 h-10 w-10 rounded-full p-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      trackEvent(TrackingEventName.CTA_CLICKED, {
                        location: 'Homepage Hero',
                        link: '/subscribe',
                      });

                      handleSearch();
                    }}
                  >
                    {isExecutingAi ? (
                      <Spinner className="h-6 w-6 animate-spin fill-white" />
                    ) : (
                      <ArrowForwardIos className="h-6 w-6" />
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <HeroFilters primaryCTA={data.primary_cta} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
